import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import FinancialDashboardNavigation from 'components/FinancialCheckup/FinancialDashboardNavigation';
import Input from 'components/Input';
import Button from 'components/Button';
import { FlowComponentType } from 'routes/types';
import { useNavigate } from 'hooks/useNavigate';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData } from 'selectors/getApplicationData';
import { updateEmploymentData } from 'thunks';
import { RoutePath } from 'enums/Routes';
import { getMessageForRequiredFields } from 'utils/errors';
import FormContainer from 'components/LoanForm/FormContainer';

import styles from './UpdateEmployer.module.scss';

enum FormField {
  EmployerName = 'employerName',
}

const EMPLOYER_NAME_LABEL = 'Primary employer name';

const UpdateEmployer = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const navigate = useNavigate();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { loanId } = useParams<{ loanId: string }>();
  const { application } = useSelector(getApplicationData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    formState: { errors, isValid },
    register,
    watch,
    trigger,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      [FormField.EmployerName]: '',
    },
  });

  const watcher = watch();

  useEffect(() => {
    register(FormField.EmployerName, {
      required: getMessageForRequiredFields(EMPLOYER_NAME_LABEL),
    });
  }, [register]);

  const handleContinue = async () => {
    if (!application) return;

    setIsSubmitting(true);

    try {
      await dispatchWithUnwrap(
        updateEmploymentData({
          applicationId: application.id,
          employmentData: {
            employment: [
              {
                employerName: watcher[FormField.EmployerName],
              },
              {},
            ],
          },
        }),
      );

      handleNext();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackClick = () => {
    navigate(generatePath(RoutePath.PaymentSettings, { loanId }) as RoutePath);
  };

  return (
    <>
      <FinancialDashboardNavigation {...navigationInfo} onBackClick={handleBackClick} />
      <FormContainer title="Who is your new employer?" className={styles.content}>
        <Input
          label={EMPLOYER_NAME_LABEL}
          placeholder={EMPLOYER_NAME_LABEL}
          errorMessage={errors[FormField.EmployerName]?.message}
          name={FormField.EmployerName}
          onChange={(event: React.FocusEvent<HTMLInputElement>) => {
            setValue(FormField.EmployerName, event.target.value);
            trigger(FormField.EmployerName);
          }}
          value={watcher[FormField.EmployerName]}
          onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
          autoFocus
        />
        <Button onClick={handleContinue} disabled={!isValid || isSubmitting} isLoading={isSubmitting}>
          Next
        </Button>
      </FormContainer>
    </>
  );
};

export default UpdateEmployer;
